/**
 * API Service - Backend Communication
 *
 * Communicates with FastAPI backend for:
 * - Municipality lookup (AI Tools)
 * - Party history lookup (AI Tools)
 * - Analysis requests
 */

import axios from 'axios';
import { getIdTokenHeaders } from './authHeaders';
import {
  MunicipalityResult,
  PartyResult,
  AnalysisRequest,
  AnalysisResponse,
  CandidateUploadRequest,
  CandidateUploadResponse,
  CandidateListResponse,
  SenateDistrictInfo,
  SurveyGenerateRequest,
  SurveyGenerateResponse,
  SurveyTemplate,
  SurveyListResponse,
  SurveyResults
} from '../types';

// Backend API URL (configure via environment variable)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 90000, // 90 second timeout (backend processing takes ~43s for Havlíčkův Brod)
  withCredentials: false, // Don't send cookies/credentials (not needed for public API)
});

// ============================================================================
// AI Tools Endpoints
// ============================================================================

/**
 * Find Municipality (Fuzzy Search)
 *
 * Example:
 *   const results = await findMunicipality('havlickuv brod');
 *   // Returns: [{ code: "568414", name: "Havlíčkův Brod", ... }]
 */
export async function findMunicipality(
  query: string,
  limit: number = 5
): Promise<MunicipalityResult[]> {
  try {
    const response = await api.get('/api/tools/find_municipality', {
      params: { query, limit },
    });
    return response.data;
  } catch (error) {
    console.error('Municipality lookup error:', error);
    throw error;
  }
}

/**
 * Get Party History for Municipality
 * Year is auto-determined by backend based on election type
 *
 * Example:
 *   const parties = await getPartyHistory('568414', 'komunalni');
 *   // Returns: [{ party_name: "ANO 2011", votes: 3421, ... }]
 */
export async function getPartyHistory(
  municipalityCode: string,
  electionType: string = 'komunalni'
): Promise<PartyResult[]> {
  try {
    const response = await api.get('/api/tools/party_history', {
      params: {
        code: municipalityCode,
        election_type: electionType,
        // year auto-determined by backend
      },
    });
    return response.data;
  } catch (error) {
    console.error('Party history lookup error:', error);
    throw error;
  }
}

/**
 * Get Senate District Info for Municipality
 *
 * Returns information about which senate district a municipality belongs to,
 * including list of all municipalities in that district.
 *
 * Example:
 *   const info = await getSenateDistrictInfo('568414');
 *   // Returns: { district_id: 52, district_name: "Žďár nad Sázavou", municipality_count: 89, ... }
 */
export async function getSenateDistrictInfo(
  municipalityCode: string
): Promise<SenateDistrictInfo> {
  try {
    const response = await api.get(`/api/tools/senate-district/${municipalityCode}`);
    return response.data;
  } catch (error) {
    console.error('Senate district info lookup error:', error);
    throw error;
  }
}

/**
 * List All Senate Districts
 *
 * Returns all 81 senate districts for selection.
 * Used when user selects "Senátní volby" election type.
 *
 * Example:
 *   const districts = await listSenateDistricts();
 *   // Returns: [{ district_id: 1, district_name: "Karlovy Vary", election_year: 2024 }, ...]
 */
export interface SenateDistrictListItem {
  district_id: number;
  district_name: string;
  election_year: number;
}

export async function listSenateDistricts(): Promise<SenateDistrictListItem[]> {
  try {
    const response = await api.get('/api/tools/senate-districts');
    return response.data;
  } catch (error) {
    console.error('Senate districts list error:', error);
    throw error;
  }
}

// ============================================================================
// Analysis Request Endpoint
// ============================================================================

/**
 * Request Electoral Analysis
 *
 * Triggers complete pipeline:
 * 1. Load electoral data
 * 2. Transform with Phoenix
 * 3. Generate HTML dashboard
 * 4. Send email with attachment
 *
 * Example:
 *   const response = await requestAnalysis({
 *     email: 'user@example.com',
 *     municipality_code: '568414',
 *     election_type: 'komunalni',
 *     year: 2022
 *   });
 */
export async function requestAnalysis(
  request: AnalysisRequest
): Promise<AnalysisResponse> {
  // Retry logic with exponential backoff for transient 503 errors
  // (Cloud Run instance busy with concurrent request, waiting for autoscaler)
  const maxRetries = 15;
  const baseDelay = 2000; // Start with 2 seconds

  console.log('[API] requestAnalysis called with:', request);
  console.log('[API] Target URL:', API_BASE_URL + '/api/analysis/request');

  let authHeaders: Record<string, string> | undefined;
  try {
    authHeaders = await getIdTokenHeaders() as Record<string, string>;
  } catch {
    // not signed in — proceed without auth header (non-SP analyses still work)
  }

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`[API] Analysis request attempt ${attempt}/${maxRetries}...`);
      console.log('[API] Sending POST request...');

      const response = await api.post('/api/analysis/request', request, authHeaders ? { headers: authHeaders } : undefined);

      console.log('[API] ✅ Request succeeded!', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });

      return response.data;
    } catch (error: any) {
      const isLastAttempt = attempt === maxRetries;
      const is503 = error.response?.status === 503;
      const is502 = error.response?.status === 502;
      const isNetworkError = error.code === 'ERR_NETWORK' || error.message === 'Network Error';
      const isRetryable = is503 || is502 || isNetworkError;

      console.error(`[API] ❌ Attempt ${attempt} failed:`, {
        code: error.code,
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
          timeout: error.config?.timeout
        }
      });

      // Retry transient errors (503, 502, Network Error) with exponential backoff
      if (isRetryable && !isLastAttempt) {
        // Exponential backoff: 2s, 4s, 8s, 16s, etc. (max ~30s between retries)
        const delay = Math.min(baseDelay * Math.pow(2, attempt - 1), 30000);
        console.log(`[API] Retrying in ${delay}ms (attempt ${attempt}/${maxRetries})...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      // Last attempt or non-retryable error - throw
      if (is503 || isNetworkError) {
        throw new Error('Backend nedostupný po několika pokusech. Zkuste to prosím za chvíli.');
      }
      throw error;
    }
  }

  // Should never reach here, but TypeScript needs it
  throw new Error('Analysis request failed after all retries');
}

// ============================================================================
// Candidate Upload Endpoints
// ============================================================================

/**
 * Upload Candidate Text
 *
 * Uploads free-form text about a candidate for AI extraction.
 * The AI extracts structured profile including SWOT, priorities, and demographics.
 *
 * Example:
 *   const response = await uploadCandidate({
 *     district_code: '568414',
 *     election_type: 'komunalni',
 *     target_year: 2026,
 *     raw_text: 'Jan Novák je 45letý podnikatel...',
 *     text_type: 'biography'
 *   });
 */
export async function uploadCandidate(
  request: CandidateUploadRequest
): Promise<CandidateUploadResponse> {
  try {
    console.log('[API] uploadCandidate called with:', request);
    const response = await api.post('/api/analysis/upload-candidate', request);
    console.log('[API] uploadCandidate response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Candidate upload error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Upload failed');
  }
}

/**
 * List Candidates for a Campaign
 *
 * Returns all uploaded candidate profiles for a given district and election year.
 *
 * Example:
 *   const candidates = await listCandidates('568414', 2026);
 */
export async function listCandidates(
  districtCode: string,
  targetYear: number
): Promise<CandidateListResponse> {
  try {
    console.log('[API] listCandidates called:', { districtCode, targetYear });
    const response = await api.get(`/api/analysis/candidates/${districtCode}/${targetYear}`);
    console.log('[API] listCandidates response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('List candidates error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to list candidates');
  }
}

// ============================================================================
// Health Check
// ============================================================================

/**
 * Check API Health
 */
export async function checkHealth(): Promise<any> {
  try {
    const response = await api.get('/api/analysis/health');
    return response.data;
  } catch (error) {
    console.error('Health check error:', error);
    throw error;
  }
}

// ============================================================================
// User Notification Endpoints
// ============================================================================

/**
 * Notify Admin of New User Registration
 *
 * Sends email to admin when a new user registers.
 */
export async function notifyNewRegistration(
  userEmail: string,
  userName: string,
  localAssociation?: string
): Promise<{ status: string; message: string }> {
  try {
    console.log('[API] notifyNewRegistration called:', { userEmail, userName });
    const response = await api.post('/api/users/notify-new-registration', {
      user_email: userEmail,
      user_name: userName,
      local_association: localAssociation
    });
    console.log('[API] notifyNewRegistration response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('New registration notification error:', error);
    // Don't throw - notification failure shouldn't block registration
    return { status: 'error', message: error.message };
  }
}

/**
 * Notify User of Approval/Rejection
 *
 * Sends email to user when their account is approved or rejected.
 */
export async function notifyApproval(
  userEmail: string,
  userName: string,
  approved: boolean
): Promise<{ status: string; message: string }> {
  try {
    console.log('[API] notifyApproval called:', { userEmail, userName, approved });
    const response = await api.post('/api/users/notify-approval', {
      user_email: userEmail,
      user_name: userName,
      approved: approved
    });
    console.log('[API] notifyApproval response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Approval notification error:', error);
    // Don't throw - notification failure shouldn't block approval
    return { status: 'error', message: error.message };
  }
}

// ============================================================================
// Firebase Admin User Management Endpoints
// ============================================================================

/**
 * Firebase Auth User type returned from backend
 */
export interface FirebaseAuthUser {
  uid: string;
  email: string;
  email_verified: boolean;
  display_name: string | null;
  created_at: string | null;
  last_sign_in: string | null;
  disabled: boolean;
}

/**
 * List All Firebase Auth Users
 *
 * Returns all users from Firebase Authentication (not Firestore).
 * This includes users who may not have Firestore documents.
 */
export async function listAllFirebaseUsers(): Promise<FirebaseAuthUser[]> {
  try {
    console.log('[API] listAllFirebaseUsers called');
    const response = await api.get('/api/users/list-all');
    console.log('[API] listAllFirebaseUsers response:', response.data);
    return response.data.users || [];
  } catch (error: any) {
    console.error('List Firebase users error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to list users');
  }
}

/**
 * Delete Firebase Auth User
 *
 * Permanently deletes a user from Firebase Authentication.
 * This is the server-side deletion that actually removes the account.
 */
export async function deleteFirebaseUser(
  uid: string,
  adminEmail: string
): Promise<{ status: string; message: string }> {
  try {
    console.log('[API] deleteFirebaseUser called:', { uid, adminEmail });
    const response = await api.delete(`/api/users/${uid}`, {
      params: { admin_email: adminEmail }
    });
    console.log('[API] deleteFirebaseUser response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Delete Firebase user error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to delete user');
  }
}

/**
 * Get Firebase Auth User by Email
 *
 * Find a user in Firebase Auth by their email address.
 */
export async function getFirebaseUserByEmail(
  email: string
): Promise<FirebaseAuthUser | null> {
  try {
    console.log('[API] getFirebaseUserByEmail called:', { email });
    const response = await api.get(`/api/users/by-email/${encodeURIComponent(email)}`);
    console.log('[API] getFirebaseUserByEmail response:', response.data);
    return response.data.user || null;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error('Get Firebase user by email error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to get user');
  }
}

// ============================================================================
// Survey Endpoints
// ============================================================================

/**
 * Generate Survey using AI
 *
 * Creates a new survey with AI-generated questions relevant to the municipality.
 */
export async function generateSurvey(
  request: SurveyGenerateRequest
): Promise<SurveyGenerateResponse> {
  try {
    const headers = await getIdTokenHeaders();
    const response = await api.post('/api/survey/generate', request, {
      headers: headers as Record<string, string>,
      timeout: 180000, // 180s - Gemini + Google Search grounding can take 60-120s
    });
    return response.data;
  } catch (error: any) {
    console.error('Generate survey error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to generate survey');
  }
}

/**
 * Get Survey by ID
 *
 * Returns survey template (public endpoint for respondents).
 */
export async function getSurvey(surveyId: string): Promise<SurveyTemplate> {
  try {
    const response = await api.get(`/api/survey/${surveyId}`);
    return response.data;
  } catch (error: any) {
    console.error('Get survey error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to get survey');
  }
}

/**
 * Update Survey
 *
 * Update survey title, description, questions, or active status (owner/admin only).
 */
export async function updateSurvey(
  surveyId: string,
  updates: Partial<SurveyTemplate>
): Promise<SurveyTemplate> {
  try {
    const headers = await getIdTokenHeaders();
    const response = await api.put(`/api/survey/${surveyId}`, updates, {
      headers: headers as Record<string, string>,
    });
    return response.data;
  } catch (error: any) {
    console.error('Update survey error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to update survey');
  }
}

/**
 * Delete Survey
 *
 * Delete survey and all responses (owner/admin only).
 */
export async function deleteSurvey(surveyId: string): Promise<void> {
  try {
    const headers = await getIdTokenHeaders();
    await api.delete(`/api/survey/${surveyId}`, {
      headers: headers as Record<string, string>,
    });
  } catch (error: any) {
    console.error('Delete survey error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to delete survey');
  }
}

/**
 * List User's Surveys
 *
 * Returns all surveys owned by the user.
 */
export async function listSurveys(): Promise<SurveyListResponse> {
  try {
    const headers = await getIdTokenHeaders();
    const response = await api.get('/api/survey/list', {
      headers: headers as Record<string, string>,
    });
    return response.data;
  } catch (error: any) {
    console.error('List surveys error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to list surveys');
  }
}

/**
 * Get Survey Results
 *
 * Returns aggregated results for a survey (owner/admin only).
 */
export async function getSurveyResults(surveyId: string): Promise<SurveyResults> {
  try {
    const headers = await getIdTokenHeaders();
    const response = await api.get(`/api/survey/${surveyId}/results`, {
      headers: headers as Record<string, string>,
    });
    return response.data;
  } catch (error: any) {
    console.error('Get survey results error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to get survey results');
  }
}

/**
 * List Public Surveys
 *
 * Returns all active surveys — public endpoint, no authentication required.
 * Used by zapojsedovoleb.cz landing page to show available surveys.
 */
export async function listPublicSurveys(): Promise<SurveyListResponse> {
  try {
    const response = await api.get('/api/survey/public');
    return response.data;
  } catch (error: any) {
    console.error('List public surveys error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Failed to list surveys');
  }
}

/**
 * Submit Survey Response (Public)
 *
 * Submit answers to a survey. No authentication required.
 */
export async function submitSurveyResponse(
  surveyId: string,
  answers: Record<string, any>
): Promise<{ response_id: string; message: string }> {
  try {
    console.log('[API] submitSurveyResponse called:', { surveyId, answers });
    const response = await api.post(`/api/survey/${surveyId}/respond`, { answers });
    console.log('[API] submitSurveyResponse response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Submit survey response error:', error);
    throw new Error(error.response?.data?.detail || error.message || 'Nepodařilo se odeslat odpověď');
  }
}

export default api;
